
import React from "react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import AccountingSystemCard from "./components/AccountingSystemCard";
import PayrollCard from "./components/PayrollCard";
import SettingsCard from "./components/SettingsCard";
import IntegrationDialog from "./components/IntegrationDialog";
import AccountingIntegrationCard from "./components/AccountingIntegrationCard";
import { KeyValuePair } from "./components/SettingsTable";
import { AvailableIntegration, Integration, deleteIntegration } from "@/api/IntegrationsAPI"

interface AccountingTabProps {
  propertyId: string | undefined;
  currentIntegration?: Integration;
  setCurrentIntegration?: (integration: Integration) => void;
  availableIntegrations?: AvailableIntegration[];
  onDeleteIntegration: () => void;
}

const AccountingTab: React.FC <AccountingTabProps> = ({ propertyId, currentIntegration, availableIntegrations, onDeleteIntegration }) => {
  const { toast } = useToast();
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  // const [accountingSettings, setAccountingSettings] = useState<KeyValuePair[]>([
  //   { id: "1", key: "API_URL", value: "https://accounting.example.com/api", description: "Accounting API URL" },
  //   { id: "2", key: "API_KEY", value: "************", description: "Accounting API Key" },
  // ]);

  const [newAccountingSetting, setNewAccountingSetting] = useState<{
    key: string;
    value: string;
    description: string;
  }>({
    key: "",
    value: "",
    description: "",
  });

  const handleAddAccountingSetting = () => {
    if (!newAccountingSetting.key || !newAccountingSetting.value) {
      toast({
        title: "Error",
        description: "Key and Value are required fields.",
        variant: "destructive",
      });
      return;
    }

    const newSetting: KeyValuePair = {
      id: Date.now().toString(),
      ...newAccountingSetting,
    };

    // setAccountingSettings([...accountingSettings, newSetting]);
    setNewAccountingSetting({ key: "", value: "", description: "" });

    toast({
      title: "Success",
      description: "Accounting setting added successfully",
    });
  };

  const handleAddAccountingIntegration = () => {
    setIntegrationDialogOpen(true);
  };

  const handleDeleteAccountingIntegration = () => {
    // Show confirmation toast
    toast({
      title: "Confirm Deletion",
      description: `Are you sure you want to delete the ${currentIntegration?.name} integration?`,
      action: (
        <div className="flex space-x-2">
          <button 
            className="rounded bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground"
            onClick={() => confirmDeleteIntegration()}
          >
            Delete
          </button>
          <button 
            className="rounded bg-secondary px-3 py-2 text-sm font-medium"
            onClick={() => toast({ title: "Cancelled", description: "Integration deletion cancelled" })}
          >
            Cancel
          </button>
        </div>
      ),
    });
  };

  const confirmDeleteIntegration = async () => {
    if (!propertyId || !currentIntegration?.id) {
      toast({
        title: "Error",
        description: "Property ID or Integration ID is missing.",
        variant: "destructive",
      });
      return;
    }
  
    try {
      // Call the API to delete the integration

      await deleteIntegration(currentIntegration.id);
  
      toast({
        title: "Integration Deleted",
        description: `The ${currentIntegration.name} integration has been removed.`,
      });
      onDeleteIntegration();
      
    } catch (error) {
      console.error("Error deleting integration:", error);
  
      // Show error toast
      toast({
        title: "Error",
        description: "Failed to delete the integration. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleXeroIntegration = () => {
    toast({
      title: "Xero Integration",
      description: "Starting OAuth2 flow with Xero...",
    });
    // Here you would initiate the OAuth2 flow with Xero
    const xeroIntId = import.meta.env.VITE_XERO_ID
    //retrieve the clientid from the integration with an id of xeroIntId
    const integration = availableIntegrations.find((integration) => integration.id === xeroIntId);
    if (!integration) {
      toast({
        title: "Error",
        description: "Integration not found.",
        variant: "destructive",
      });
      return;
    }
    window.location.href = `https://login.xero.com/identity/connect/authorize?response_type=code&client_id=${integration.clientId}&redirect_uri=${integration.callback}&scope=offline_access openid profile payroll.payruns.read payroll.timesheets.read payroll.payslip.read payroll.employees.read&state=${integration.id}:${propertyId}`;

    setIntegrationDialogOpen(false);  
  };

  const handleMyobIntegration = () => {
    toast({
      title: "MYOB Integration",
      description: "Starting OAuth2 flow with MYOB...",
    });
    // Here you would initiate the OAuth2 flow with MYOB
    const intId = import.meta.env.VITE_MYOB_ID
    const integration = availableIntegrations.find((integration) => integration.id === intId);
    if (!integration) {
      toast({
        title: "Error",
        description: "MYOB integration not found.",
        variant: "destructive",
      });
      return;
    }
    window.location.href = `https://secure.myob.com/oauth2/account/authorize?client_id=${integration.clientId}&redirect_uri=${integration.callback}&response_type=code&scope=offline_access openid sme-company-file sme-general-ledger sme-payroll&state=${integration.id}:${propertyId}` 

    setIntegrationDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      <AccountingSystemCard 
        onAddIntegration={handleAddAccountingIntegration} 
        onDeleteIntegration={handleDeleteAccountingIntegration}
        currentIntegration={currentIntegration}
        availableIntegrations={availableIntegrations}
      />
      {/* Accounting connections card to map external accounting connections */}
      <AccountingIntegrationCard propertyId={propertyId} currentIntegration={currentIntegration} />
      <PayrollCard propertyId={propertyId} currentIntegration={currentIntegration} />
      
      <IntegrationDialog
        open={integrationDialogOpen}
        onOpenChange={setIntegrationDialogOpen}
        onXeroIntegration={handleXeroIntegration}
        onMyobIntegration={handleMyobIntegration}
      />
      
      {/* Employees card moved to the Employees tab */}
      {/* <SettingsCard
        title="Accounting Settings"
        description="Manage accounting integration settings"
        settings={accountingSettings}
        newSetting={newAccountingSetting}
        onNewSettingKeyChange={(key) => setNewAccountingSetting({ ...newAccountingSetting, key })}
        onNewSettingValueChange={(value) => setNewAccountingSetting({ ...newAccountingSetting, value })}
        onNewSettingDescriptionChange={(description) => setNewAccountingSetting({ ...newAccountingSetting, description })}
        onAddSetting={handleAddAccountingSetting}
        idPrefix="accounting"
        tableCaption="Accounting configuration settings"
      /> */}
    </div>
  );
};

export default AccountingTab;
